import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CANDIDATE_STATUS, EVALUATION_ROUNDS } from '@/types/enums';
import { formatCandidateStatus, formatEvaluationRound } from '@/utils/formatters';
import { getCandidateStatusHint } from '@/utils/candidateStatus';
import type { Tables } from '@/integrations/supabase/types';

type Candidate = Tables<'candidates'>;

const AUTO_ASSIGN_PANELIST_STATUSES = new Set<string>([
  CANDIDATE_STATUS.PASSED,
  CANDIDATE_STATUS.PENDING,
  CANDIDATE_STATUS.HOLD,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
]);

interface FormData {
  status: string;
  current_round: string;
  disable_ip_validation: boolean;
}

interface CandidateStatusUpdateFormProps {
  candidate: Candidate;
  onSaved?: () => void;
}

export function CandidateStatusUpdateForm({ candidate, onSaved }: CandidateStatusUpdateFormProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, setValue, watch } = useForm<FormData>({
    defaultValues: {
      status: candidate.status,
      current_round: candidate.current_round || '',
      disable_ip_validation: candidate.disable_ip_validation ?? false,
    },
  });

  const statusOptions =
    candidate.current_round === EVALUATION_ROUNDS.HR_FOUNDER
      ? [
          CANDIDATE_STATUS.OFFERED,
          CANDIDATE_STATUS.SELECTED,
          CANDIDATE_STATUS.DECLINED,
          CANDIDATE_STATUS.ACCEPTED,
          CANDIDATE_STATUS.JOINED,
          CANDIDATE_STATUS.HOLD,
        ]
      : [CANDIDATE_STATUS.PASSED, CANDIDATE_STATUS.FAILED, CANDIDATE_STATUS.HOLD];

  const onSubmit = async (data: FormData) => {
    setIsSaving(true);
    try {
      const updateData: {
        status: any;
        current_round: string;
        disable_ip_validation: boolean;
        interviewer?: string[] | null;
      } = {
        status: data.status as any,
        current_round: data.current_round,
        disable_ip_validation: data.disable_ip_validation,
      };

      const hasExplicitInterviewer = Array.isArray(candidate.interviewer)
        ? candidate.interviewer.length > 0
        : typeof candidate.interviewer === 'string' && candidate.interviewer.trim().length > 0;

      if (!hasExplicitInterviewer && AUTO_ASSIGN_PANELIST_STATUSES.has(data.status) && candidate.job_position_id) {
        const { data: jobPosition, error: jobPositionError } = await supabase
          .from('job_positions')
          .select('interviewer')
          .eq('id', candidate.job_position_id)
          .maybeSingle();

        if (jobPositionError) throw jobPositionError;

        const defaultInterviewers = Array.isArray(jobPosition?.interviewer)
          ? jobPosition.interviewer.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
          : typeof jobPosition?.interviewer === 'string' && jobPosition.interviewer.trim().length > 0
            ? [jobPosition.interviewer]
            : [];

        if (defaultInterviewers.length > 0) {
          updateData.interviewer = defaultInterviewers;
        }
      }

      const { error } = await supabase
        .from('candidates')
        .update(updateData)
        .eq('id', candidate.id);
      if (error) throw error;
      toast({ title: 'Updated', description: 'Candidate status updated' });
      onSaved?.();
    } catch (err) {
      console.error('Update error:', err);
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Update Status &amp; Next Round</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="candidate_status">Status</Label>
            <Select onValueChange={(value) => setValue('status', value)} defaultValue={candidate.status}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {formatCandidateStatus(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {getCandidateStatusHint(candidate.current_round)}
            </p>
          </div>

          <div>
            <Label htmlFor="current_round">Next Round</Label>
            <Select onValueChange={(value) => setValue('current_round', value)} defaultValue={candidate.current_round || ''}>
              <SelectTrigger>
                <SelectValue placeholder="Select round" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(EVALUATION_ROUNDS).map((round) => (
                  <SelectItem key={round} value={round}>
                    {formatEvaluationRound(round)}
                  </SelectItem>
                ))}
              </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor="disable_ip_validation">Disable IP Validation</Label>
          <Switch
            id="disable_ip_validation"
            checked={watch('disable_ip_validation')}
            onCheckedChange={(val) => setValue('disable_ip_validation', val)}
          />
        </div>

        <Button type="submit" disabled={isSaving} className="w-full">
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        </form>
      </CardContent>
    </Card>
  );
}
